import { Injectable } from '@angular/core';

export type ParentRelation = {
  childEntity: string; // Entity containing the dropdown field
  parentEntity: string; // Entity being referenced (from entity_name)
  childFieldName: string; // Name of the dropdown field in child
  parentFieldName: string; // Field name in parent entity (from field_name)
  relationType: 'parent'; // Always 'parent' for parent relations
};

export type ChildRelation = {
  parentEntity: string; // Entity that has child entities
  childEntity: string; // Name of the child entity (from field name)
  parentFieldName: string; // Name of parent field (from nop)
  childFieldName: string; // Name of child field (from nic)
  relationType: 'child'; // Always 'child' for child relations
};

export type EntityRelation = ParentRelation | ChildRelation;

export type EntityLink = {
  childEntity: string;
  parentEntity: string;
  childForeignKeyField: string;
  parentForeignKeyField?: string;
  linkType: 'dropdown' | 'entity'; // Type of relationship
};

export type EntityGroup = {
  id: string;
  entities: string[];
  links: EntityLink[];
  parentRelations: ParentRelation[];
  childRelations: ChildRelation[];
};

@Injectable({ providedIn: 'root' })
export class EntityRelationService {
  // Cache per process/org: key -> groups, relations, and link indexes
  private relationsCache = new Map<string, { 
    groups: EntityGroup[]; 
    byEntity: Map<string, EntityGroup>; 
    links: EntityLink[];
    parentRelations: ParentRelation[];
    childRelations: ChildRelation[];
  }>();

  private getCacheKey(orgRouter: string, processRouter: string): string {
    return `${orgRouter}___${processRouter}`;
  }

  clear(orgRouter: string, processRouter: string): void {
    this.relationsCache.delete(this.getCacheKey(orgRouter, processRouter));
  }

  /**
   * Build groups of related entities and relationships from metadata.
   * - Discovers parent relations from dropdown ENTITY_DATA fields
   * - Discovers child relations from entity datatype fields with nic/nop
   */
  buildFromMetadata(orgRouter: string, processRouter: string, metadata: any): void {
    const key = this.getCacheKey(orgRouter, processRouter);
    if (!metadata || !Array.isArray(metadata?.entities)) {
      this.relationsCache.set(key, { 
        groups: [], 
        byEntity: new Map(), 
        links: [],
        parentRelations: [],
        childRelations: []
      });
      return;
    }

    const entities: any[] = metadata.entities;
    const links: EntityLink[] = [];
    const parentRelations: ParentRelation[] = [];
    const childRelations: ChildRelation[] = [];

    // Discover parent relations via dropdown ENTITY_DATA fields
    for (const entity of entities) {
      const childEntityName: string = entity?.name;
      
      for (const field of entity?.fields || []) {
        // Parent relation: dropdown with entity_data
        const isDropdown = field?.datatype === 'dropdown_single_select' || field?.datatype === 'dropdown_multi_select';
        if (isDropdown && field?.option_type === 'ENTITY_DATA' && field?.entity_name && field?.field_name) {
          const parentRelation: ParentRelation = {
            childEntity: childEntityName,
            parentEntity: field.entity_name,
            childFieldName: field.name,
            parentFieldName: field.field_name,
            relationType: 'parent'
          };
          parentRelations.push(parentRelation);
          
          // Also create a link for backward compatibility
          links.push({
            childEntity: childEntityName,
            parentEntity: field.entity_name,
            childForeignKeyField: field.name,
            parentForeignKeyField: field.field_name,
            linkType: 'dropdown'
          });
        }

        // Child relation: entity datatype with nic/nop
        if (field?.datatype === 'entity' && field?.nic && field?.nop) {
          const childRelation: ChildRelation = {
            parentEntity: childEntityName,
            childEntity: field.name, // The field name is the child entity name
            parentFieldName: field.nop, // name of parent field
            childFieldName: field.nic, // name of child field
            relationType: 'child'
          };
          childRelations.push(childRelation);
          
          // Also create a link for backward compatibility
          links.push({
            childEntity: field.name,
            parentEntity: childEntityName,
            childForeignKeyField: field.nic,
            parentForeignKeyField: field.nop,
            linkType: 'entity'
          });
        }
      }
    }

    // DEBUG: log discovered relations
    try {
      // eslint-disable-next-line no-console
      //console.log('[EntityRelation] Parent relations', parentRelations);
      // eslint-disable-next-line no-console
      //console.log('[EntityRelation] Child relations', childRelations);
      // eslint-disable-next-line no-console
      //console.log('[EntityRelation] Links', links);
    } catch {}

    // Build groups (connected components) from all relationships
    const adjacency = new Map<string, Set<string>>();
    const ensure = (name: string) => {
      if (!adjacency.has(name)) adjacency.set(name, new Set());
    };
    
    // Ensure all entities are in the adjacency map
    for (const e of entities) ensure(e.name);
    
    // Add connections from all links
    for (const link of links) {
      ensure(link.childEntity);
      ensure(link.parentEntity);
      adjacency.get(link.childEntity)!.add(link.parentEntity);
      adjacency.get(link.parentEntity)!.add(link.childEntity);
    }

    const visited = new Set<string>();
    const groups: EntityGroup[] = [];

    for (const e of entities) {
      const name = e.name;
      if (visited.has(name)) continue;
      
      // BFS to find connected component
      const queue = [name];
      visited.add(name);
      const component: string[] = [];
      
      while (queue.length) {
        const cur = queue.shift()!;
        component.push(cur);
        for (const nxt of adjacency.get(cur) || []) {
          if (!visited.has(nxt)) {
            visited.add(nxt);
            queue.push(nxt);
          }
        }
      }
      
      // Filter relations and links for this group
      const groupLinks = links.filter(l => 
        component.includes(l.childEntity) || component.includes(l.parentEntity)
      );
      const groupParentRelations = parentRelations.filter(r => 
        component.includes(r.childEntity) || component.includes(r.parentEntity)
      );
      const groupChildRelations = childRelations.filter(r => 
        component.includes(r.parentEntity) || component.includes(r.childEntity)
      );
      
      groups.push({
        id: component.sort().join('___'),
        entities: component.sort(),
        links: groupLinks,
        parentRelations: groupParentRelations,
        childRelations: groupChildRelations
      });
    }

    const byEntity = new Map<string, EntityGroup>();
    for (const g of groups) {
      for (const n of g.entities) byEntity.set(n, g);
    }

    this.relationsCache.set(key, { 
      groups, 
      byEntity, 
      links,
      parentRelations,
      childRelations
    });
    
    // DEBUG
    // eslint-disable-next-line no-console
    // console.log('[EntityRelation] Built groups', { 
    //   key, 
    //   groupsCount: groups.length, 
    //   parentRelationsCount: parentRelations.length,
    //   childRelationsCount: childRelations.length,
    //   groups 
    // });
  }

  /** Return the group for an entity, if any (after buildFromMetadata) */
  getGroupForEntity(orgRouter: string, processRouter: string, entityName: string): EntityGroup | undefined {
    return this.relationsCache.get(this.getCacheKey(orgRouter, processRouter))?.byEntity.get(entityName);
  }

  /** Get all links (child->parent) - for backward compatibility */
  getLinks(orgRouter: string, processRouter: string): EntityLink[] {
    return this.relationsCache.get(this.getCacheKey(orgRouter, processRouter))?.links || [];
  }

  /** Get all parent relations (child entities referencing parent entities via dropdown) */
  getParentRelations(orgRouter: string, processRouter: string): ParentRelation[] {
    return this.relationsCache.get(this.getCacheKey(orgRouter, processRouter))?.parentRelations || [];
  }

  /** Get all child relations (parent entities having child entities) */
  getChildRelations(orgRouter: string, processRouter: string): ChildRelation[] {
    return this.relationsCache.get(this.getCacheKey(orgRouter, processRouter))?.childRelations || [];
  }

  /** Get all relations (both parent and child) */
  getAllRelations(orgRouter: string, processRouter: string): EntityRelation[] {
    const cache = this.relationsCache.get(this.getCacheKey(orgRouter, processRouter));
    if (!cache) return [];
    return [...cache.parentRelations, ...cache.childRelations];
  }

  /** Get parent relations for a specific child entity */
  getParentRelationsForChild(orgRouter: string, processRouter: string, childEntity: string): ParentRelation[] {
    return this.getParentRelations(orgRouter, processRouter).filter(r => r.childEntity === childEntity);
  }

  /** Get child relations for a specific parent entity */
  getChildRelationsForParent(orgRouter: string, processRouter: string, parentEntity: string): ChildRelation[] {
    return this.getChildRelations(orgRouter, processRouter).filter(r => r.parentEntity === parentEntity);
  }

  /** Get all entities that are parents of the given entity */
  getParentEntities(orgRouter: string, processRouter: string, childEntity: string): string[] {
    return this.getParentRelationsForChild(orgRouter, processRouter, childEntity).map(r => r.parentEntity);
  }

  /** Get all entities that are children of the given entity */
  getChildEntities(orgRouter: string, processRouter: string, parentEntity: string): string[] {
    return this.getChildRelationsForParent(orgRouter, processRouter, parentEntity).map(r => r.childEntity);
  }

  /** Check if entity A is a parent of entity B */
  isParentOf(orgRouter: string, processRouter: string, parentEntity: string, childEntity: string): boolean {
    return this.getParentRelations(orgRouter, processRouter)
      .some(r => r.parentEntity === parentEntity && r.childEntity === childEntity);
  }

  /** Check if entity A is a child of entity B */
  isChildOf(orgRouter: string, processRouter: string, childEntity: string, parentEntity: string): boolean {
    return this.isParentOf(orgRouter, processRouter, parentEntity, childEntity);
  }

  /** Get the relationship between two entities (if any) */
  getRelationBetween(orgRouter: string, processRouter: string, entityA: string, entityB: string): EntityRelation | undefined {
    const allRelations = this.getAllRelations(orgRouter, processRouter);
    return allRelations.find(r => 
      (r.relationType === 'parent' && r.childEntity === entityA && r.parentEntity === entityB) ||
      (r.relationType === 'parent' && r.childEntity === entityB && r.parentEntity === entityA) ||
      (r.relationType === 'child' && r.parentEntity === entityA && r.childEntity === entityB) ||
      (r.relationType === 'child' && r.parentEntity === entityB && r.childEntity === entityA)
    );
  }
}


